import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Box, IconButton, Typography } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { Rnd } from "react-rnd";
import { useTheme } from "@mui/material";
import ImageActions from "../ModalImageEdit/single/ImageActions";
import { fitTextInBox } from "../../../features/creativeTemplates/instance/fitTextInBox";
import ZoomControls from "../../../features/creativeTemplates/editor/ZoomControls";
import { useCanvasZoom } from "../../../features/creativeTemplates/editor/useCanvasZoom";

const CORNER_RESIZE_DIRECTIONS = new Set([
  "topLeft",
  "topRight",
  "bottomLeft",
  "bottomRight",
]);

const MIN_LAYER_SIZE_PX = 8;
const VIEWPORT_PADDING = 24;

const clientToCanvasPixels = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.offsetWidth,
    y: ((event.clientY - rect.top) / rect.height) * canvas.offsetHeight,
  };
};

const boxFromElementRect = (element, canvas) => {
  const canvasRect = canvas.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (!canvasRect.width || !canvasRect.height) {
    return {
      x: 0,
      y: 0,
      width: element.offsetWidth,
      height: element.offsetHeight,
    };
  }
  return {
    x:
      ((elementRect.left - canvasRect.left) / canvasRect.width) *
      canvas.offsetWidth,
    y:
      ((elementRect.top - canvasRect.top) / canvasRect.height) *
      canvas.offsetHeight,
    width: (elementRect.width / canvasRect.width) * canvas.offsetWidth,
    height: (elementRect.height / canvasRect.height) * canvas.offsetHeight,
  };
};

const clampBoxToCanvas = (box, canvas) => {
  const canvasWidth = canvas?.offsetWidth || 0;
  const canvasHeight = canvas?.offsetHeight || 0;
  const width = Math.min(
    Math.max(box.width, MIN_LAYER_SIZE_PX),
    canvasWidth || box.width
  );
  const height = Math.min(
    Math.max(box.height, MIN_LAYER_SIZE_PX),
    canvasHeight || box.height
  );
  const maxX = Math.max(canvasWidth - width, 0);
  const maxY = Math.max(canvasHeight - height, 0);
  return {
    x: Math.min(Math.max(box.x, 0), maxX),
    y: Math.min(Math.max(box.y, 0), maxY),
    width,
    height,
  };
};

const isPointInBox = (point, box) =>
  point.x >= box.x &&
  point.x <= box.x + box.width &&
  point.y >= box.y &&
  point.y <= box.y + box.height;

const artboardToDisplay = ({ box, scale }) => ({
  x: box.x * scale,
  y: box.y * scale,
  width: box.width * scale,
  height: box.height * scale,
});

const hitTestItem = ({ items, point, scale, selectedID, preferText = false }) => {
  const withBoxes = items
    .filter((item) => item.box.visible !== false && !item.isBackground)
    .map((item) => ({
      item,
      box: artboardToDisplay({ box: item.box, scale }),
    }));

  if (preferText) {
    const textHit = [...withBoxes]
      .reverse()
      .find(
        ({ item, box }) =>
          item.node?.type === "text" && isPointInBox(point, box)
      );
    if (textHit) return textHit.item;
  }

  const selectedHit = withBoxes.find(
    ({ item, box }) => item.id === selectedID && isPointInBox(point, box)
  );
  if (selectedHit) return selectedHit.item;

  return (
    [...withBoxes].reverse().find(({ box }) => isPointInBox(point, box))
      ?.item || null
  );
};

const HANDLE_SIZE = 10;
const HANDLE_OFFSET = -HANDLE_SIZE / 2;
const LABEL_HEIGHT = 18;
const SELECTION_COLOR = "#111";
const RESIZE_HANDLE_CLASS = "instance-canvas-resize-handle";

const LAYER_RESIZE_ENABLED = {
  top: true,
  right: true,
  bottom: true,
  left: true,
  topLeft: true,
  topRight: true,
  bottomLeft: true,
  bottomRight: true,
};

const HANDLE_STYLE = {
  width: HANDLE_SIZE,
  height: HANDLE_SIZE,
  background: SELECTION_COLOR,
  border: "1px solid #fff",
  borderRadius: 2,
  pointerEvents: "auto",
  zIndex: 2,
  boxSizing: "border-box",
};

const RESIZE_HANDLE_STYLES = {
  top: {
    ...HANDLE_STYLE,
    left: "50%",
    top: HANDLE_OFFSET,
    marginLeft: HANDLE_OFFSET,
    cursor: "ns-resize",
  },
  right: {
    ...HANDLE_STYLE,
    top: "50%",
    right: HANDLE_OFFSET,
    marginTop: HANDLE_OFFSET,
    cursor: "ew-resize",
  },
  bottom: {
    ...HANDLE_STYLE,
    left: "50%",
    bottom: HANDLE_OFFSET,
    marginLeft: HANDLE_OFFSET,
    cursor: "ns-resize",
  },
  left: {
    ...HANDLE_STYLE,
    top: "50%",
    left: HANDLE_OFFSET,
    marginTop: HANDLE_OFFSET,
    cursor: "ew-resize",
  },
  topLeft: { ...HANDLE_STYLE, left: HANDLE_OFFSET, top: HANDLE_OFFSET },
  topRight: {
    ...HANDLE_STYLE,
    right: HANDLE_OFFSET,
    top: HANDLE_OFFSET,
  },
  bottomLeft: {
    ...HANDLE_STYLE,
    left: HANDLE_OFFSET,
    bottom: HANDLE_OFFSET,
  },
  bottomRight: {
    ...HANDLE_STYLE,
    right: HANDLE_OFFSET,
    bottom: HANDLE_OFFSET,
  },
};

const RESIZE_HANDLE_CLASSES = {
  top: RESIZE_HANDLE_CLASS,
  right: RESIZE_HANDLE_CLASS,
  bottom: RESIZE_HANDLE_CLASS,
  left: RESIZE_HANDLE_CLASS,
  topLeft: RESIZE_HANDLE_CLASS,
  topRight: RESIZE_HANDLE_CLASS,
  bottomLeft: RESIZE_HANDLE_CLASS,
  bottomRight: RESIZE_HANDLE_CLASS,
};

const isResizeHandleEvent = (event) =>
  Boolean(
    event.target instanceof Element &&
      event.target.closest(`.${RESIZE_HANDLE_CLASS}`)
  );

const SelectionLabel = ({ name, placeInside }) => (
  <Typography
    sx={{
      position: "absolute",
      top: placeInside ? 0 : -LABEL_HEIGHT,
      left: 0,
      fontSize: "10.5px",
      lineHeight: `${LABEL_HEIGHT}px`,
      px: 0.75,
      borderRadius: "5px",
      bgcolor: SELECTION_COLOR,
      color: "#fff",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      zIndex: 3,
    }}
  >
    {name}
  </Typography>
);

const LayerContent = ({
  item,
  isEditingText,
  onTextCommit,
  scale,
}) => {
  const node = item.node;
  switch (node.type) {
    case "image":
      return item.url ? (
        <Box
          component="img"
          src={item.url}
          alt={node.name || ""}
          draggable={false}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: node.fit === "contain" ? "contain" : "fill",
            display: "block",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      ) : null;
    case "shape":
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: node.shape?.fill || "#000",
            borderRadius: `${Number(node.shape?.radius) || 0}px`,
            pointerEvents: "none",
          }}
        />
      );
    case "group":
      return null;
    case "text": {
      const orientation =
        String(node.style?.orientation || "horizontal") === "vertical"
          ? "vertical"
          : "horizontal";
      const fontFamily = node.style?.fontAssetRef || "sans-serif";
      const requestedFontSize = Number(node.style?.fontSize);
      const hasRequestedFontSize =
        Number.isFinite(requestedFontSize) && requestedFontSize > 0;
      const layout = fitTextInBox({
        text: item.text,
        boxWidth: item.box.width,
        boxHeight: item.box.height,
        fontFamily,
        minimumLines: node.overflow?.minimumLines,
        maximumLines: node.overflow?.maximumLines,
        minimumFontSize: node.overflow?.minimumFontSize,
        requestedFontSize: hasRequestedFontSize
          ? requestedFontSize
          : undefined,
        lineHeightMult: node.style?.lineHeight,
        orientation,
      });
      const fontSize =
        (hasRequestedFontSize
          ? requestedFontSize
          : layout.ok
            ? layout.fontSize
            : 12) * scale;
      const align = node.style?.align || "left";
      const textSx = {
        width: "100%",
        height: "100%",
        outline: "none",
        color: node.style?.color || "#fff",
        fontFamily: `"${fontFamily}"`,
        fontSize: `${fontSize}px`,
        lineHeight: layout.ok ? layout.lineHeightMult : 1.2,
        textAlign: align,
        whiteSpace: orientation === "vertical" ? "normal" : "pre-wrap",
        writingMode:
          orientation === "vertical" ? "vertical-rl" : "horizontal-tb",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        alignContent: "center",
        justifyContent:
          align === "center"
            ? "center"
            : align === "right"
              ? "flex-end"
              : "flex-start",
        padding: 0,
        margin: 0,
        boxSizing: "border-box",
      };
      if (isEditingText) {
        return (
          <Box
            component="div"
            contentEditable
            suppressContentEditableWarning
            autoFocus
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onBlur={(event) =>
              onTextCommit(item.id, event.currentTarget.innerText)
            }
            sx={{
              ...textSx,
              pointerEvents: "auto",
              userSelect: "text",
              caretColor: node.style?.color || "#fff",
            }}
          >
            {item.text}
          </Box>
        );
      }
      return (
        <Box
          sx={{
            ...textSx,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {item.text}
        </Box>
      );
    }
    default: {
      const exhaustive = node.type;
      throw new Error(`Unhandled node type: ${exhaustive}`);
    }
  }
};

const MovableLayer = ({
  item,
  pixelBox,
  isSelected,
  isEditingText,
  canvasRef,
  canvasScale,
  paintIndex,
  onResizeOrDragStop,
  onTextCommit,
  scale,
}) => {
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const [liveBox, setLiveBox] = useState(pixelBox);
  const liveBoxRef = useRef(pixelBox);
  const isResizingRef = useRef(false);

  liveBoxRef.current = liveBox;

  useLayoutEffect(() => {
    if (isResizingRef.current) return;
    const nextBox = {
      x: pixelBox.x,
      y: pixelBox.y,
      width: pixelBox.width,
      height: pixelBox.height,
    };
    liveBoxRef.current = nextBox;
    setLiveBox(nextBox);
  }, [pixelBox.x, pixelBox.y, pixelBox.width, pixelBox.height]);

  const commitBox = (nextBox) => {
    liveBoxRef.current = nextBox;
    setLiveBox(nextBox);
    onResizeOrDragStop(item, nextBox);
  };

  const canResize = isSelected && !item.isBackground && !isEditingText;
  const placeLabelInside = liveBox.y < LABEL_HEIGHT;

  return (
    <Rnd
      bounds="parent"
      disableDragging
      enableResizing={canResize ? LAYER_RESIZE_ENABLED : false}
      scale={canvasScale}
      lockAspectRatio={lockAspectRatio}
      size={{
        width: Math.max(liveBox.width, MIN_LAYER_SIZE_PX),
        height: Math.max(liveBox.height, MIN_LAYER_SIZE_PX),
      }}
      position={{ x: liveBox.x, y: liveBox.y }}
      style={{
        zIndex: paintIndex + 1,
        border: isSelected ? `1.5px solid ${SELECTION_COLOR}` : "1px solid transparent",
        pointerEvents: isEditingText ? "auto" : "none",
        overflow: "visible",
      }}
      resizeHandleStyles={canResize ? RESIZE_HANDLE_STYLES : undefined}
      resizeHandleClasses={canResize ? RESIZE_HANDLE_CLASSES : undefined}
      onPointerDown={(event) => {
        if (isResizeHandleEvent(event) || isEditingText) {
          event.stopPropagation();
        }
      }}
      onResizeStart={(event, direction) => {
        event?.stopPropagation?.();
        isResizingRef.current = true;
        flushSync(() => {
          setLockAspectRatio(CORNER_RESIZE_DIRECTIONS.has(direction));
        });
      }}
      onResize={(_event, _direction, elementRef) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const nextBox = clampBoxToCanvas(
          boxFromElementRect(elementRef, canvas),
          canvas
        );
        liveBoxRef.current = nextBox;
        setLiveBox(nextBox);
      }}
      onResizeStop={(_event, _direction, elementRef) => {
        isResizingRef.current = false;
        const canvas = canvasRef.current;
        if (!canvas) {
          onResizeOrDragStop(item, liveBoxRef.current);
          return;
        }
        commitBox(
          clampBoxToCanvas(boxFromElementRect(elementRef, canvas), canvas)
        );
      }}
    >
      <LayerContent
        item={item}
        isEditingText={isEditingText}
        onTextCommit={onTextCommit}
        scale={scale}
      />
      {isSelected ? (
        <SelectionLabel
          name={item.node.name || item.id}
          placeInside={placeLabelInside}
        />
      ) : null}
    </Rnd>
  );
};

const InstanceCanvas = ({
  items = [],
  canvasWidth,
  canvasHeight,
  selectedLayerId,
  editingTextID,
  isSidebarOpen,
  previewUrl = "",
  onToggleSidebar,
  onSelectLayer,
  onLayerBoxChange,
  onStartTextEdit,
  onTextCommit,
  onClose,
  onCallbackSave = () => {},
  saveDisabled = true,
}) => {
  const theme = useTheme();
  const viewportRef = useRef(null);
  const artboardRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [activeDrag, setActiveDrag] = useState(null);
  const activeDragRef = useRef(null);
  activeDragRef.current = activeDrag;
  const { zoom, viewportSize, onZoomIn, onZoomOut, onFit } = useCanvasZoom({
    viewportRef,
    boardRef: artboardRef,
    resetKey: `${canvasWidth}x${canvasHeight}`,
    lockRef: activeDragRef,
  });
  const aspect =
    canvasWidth && canvasHeight ? canvasWidth / canvasHeight : 1;
  const { fitWidth, fitHeight } = useMemo(() => {
    const pad = VIEWPORT_PADDING * 2;
    const availableWidth = Math.max(1, viewportSize.width - pad);
    const availableHeight = Math.max(1, viewportSize.height - pad);
    if (availableWidth / availableHeight > aspect) {
      return {
        fitHeight: availableHeight,
        fitWidth: availableHeight * aspect,
      };
    }
    return {
      fitWidth: availableWidth,
      fitHeight: availableWidth / aspect,
    };
  }, [aspect, viewportSize.height, viewportSize.width]);
  const displaySize = {
    width: viewportSize.width ? fitWidth * zoom : 0,
    height: viewportSize.height ? fitHeight * zoom : 0,
  };
  const scale =
    canvasWidth && displaySize.width ? displaySize.width / canvasWidth : 1;

  useLayoutEffect(() => {
    const canvas = artboardRef.current;
    if (!canvas?.offsetWidth) return;
    const nextScale =
      canvas.getBoundingClientRect().width / canvas.offsetWidth;
    if (!Number.isFinite(nextScale) || nextScale <= 0) return;
    setCanvasScale((current) =>
      Math.abs(current - nextScale) < 0.001 ? current : nextScale
    );
  }, [displaySize.height, displaySize.width]);

  const onResizeOrDragStop = (item, nextBox) => {
    if (!displaySize.width || !displaySize.height || !canvasWidth) return;
    onLayerBoxChange({
      id: item.id,
      nx: nextBox.x / displaySize.width,
      ny: nextBox.y / displaySize.height,
      nw: nextBox.width / displaySize.width,
      nh: nextBox.height / displaySize.height,
    });
  };

  const onArtboardPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (editingTextID) return;
    if (isResizeHandleEvent(event)) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;

    const point = clientToCanvasPixels(event, canvas);
    const hitItem = hitTestItem({
      items,
      point,
      scale,
      selectedID: selectedLayerId,
    });
    if (!hitItem) return;

    event.preventDefault();
    onSelectLayer(hitItem.id);

    if (hitItem.isBackground) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    const box =
      activeDrag?.layerId === hitItem.id
        ? activeDrag.box
        : artboardToDisplay({ box: hitItem.box, scale });
    const nextDrag = {
      layerId: hitItem.id,
      box,
      grabOffset: {
        x: point.x - box.x,
        y: point.y - box.y,
      },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
  };

  const onArtboardPointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const point = clientToCanvasPixels(event, canvas);
    setActiveDrag((current) => {
      if (!current) return current;
      const next = {
        ...current,
        box: clampBoxToCanvas(
          {
            ...current.box,
            x: point.x - current.grabOffset.x,
            y: point.y - current.grabOffset.y,
          },
          canvas
        ),
      };
      activeDragRef.current = next;
      return next;
    });
  };

  const onArtboardPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const current = activeDragRef.current;
    if (!current) return;
    activeDragRef.current = null;
    setActiveDrag(null);
    const item = items.find((entry) => entry.id === current.layerId);
    if (item) {
      onResizeOrDragStop(item, current.box);
    }
  };

  const onArtboardDoubleClick = (event) => {
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;
    const point = clientToCanvasPixels(event, canvas);
    const hitItem = hitTestItem({
      items,
      point,
      scale,
      selectedID: selectedLayerId,
      preferText: true,
    });
    if (hitItem?.node?.type === "text") {
      onSelectLayer(hitItem.id);
      onStartTextEdit(hitItem.id);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          pt: { xs: 6, sm: 6 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          background: `linear-gradient(135deg, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)"
          } 0%, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)"
          } 100%)`,
        }}
      >
        {!isSidebarOpen ? (
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              position: "absolute",
              top: 15,
              left: 10,
              zIndex: 10,
            }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 15,
            right: 6,
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          ref={viewportRef}
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            overflow: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            overscrollBehavior: "contain",
          }}
        >
          <Box
            sx={{
              margin: "auto",
              p: `${VIEWPORT_PADDING}px`,
              flexShrink: 0,
            }}
          >
          {displaySize.width > 0 ? (
            <Box
              ref={artboardRef}
              onPointerDown={onArtboardPointerDown}
              onPointerMove={onArtboardPointerMove}
              onPointerUp={onArtboardPointerUp}
              onPointerCancel={onArtboardPointerUp}
              onDoubleClick={onArtboardDoubleClick}
              sx={{
                position: "relative",
                width: displaySize.width,
                height: displaySize.height,
                flexShrink: 0,
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#fff",
                touchAction: "none",
              }}
            >
              {items.map((item, index) => {
                if (item.box.visible === false) return null;
                const pixelBox =
                  activeDrag?.layerId === item.id
                    ? activeDrag.box
                    : artboardToDisplay({ box: item.box, scale });
                const isSelected = selectedLayerId === item.id;

                if (item.isBackground) {
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.url}
                        alt={item.node.name || ""}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "fill",
                          pointerEvents: "none",
                          display: "block",
                        }}
                      />
                      {isSelected ? (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            border: `1.5px solid ${SELECTION_COLOR}`,
                            pointerEvents: "none",
                          }}
                        >
                          <SelectionLabel
                            name={item.node.name || item.id}
                            placeInside
                          />
                        </Box>
                      ) : null}
                    </Box>
                  );
                }

                return (
                  <MovableLayer
                    key={item.id}
                    item={item}
                    pixelBox={pixelBox}
                    isSelected={isSelected}
                    isEditingText={editingTextID === item.id}
                    canvasRef={artboardRef}
                    canvasScale={canvasScale}
                    paintIndex={index}
                    onResizeOrDragStop={onResizeOrDragStop}
                    onTextCommit={onTextCommit}
                    scale={scale}
                  />
                );
              })}
            </Box>
          ) : null}
          </Box>
        </Box>
        <ZoomControls
          zoom={zoom}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onFit={onFit}
        />
      </Box>

      <ImageActions
        imageUrl={previewUrl}
        initialImageUrl={previewUrl}
        onCallbackSave={onCallbackSave}
        onClose={onClose}
        showSaveAlert={false}
        disabled={saveDisabled}
      />
    </Box>
  );
};

export default InstanceCanvas;
